import * as React from "react";
import { Link } from "@tanstack/react-router";
import {
  AlarmClock,
  ChartLine,
  ChevronDown,
  FileText,
  Hash,
  Image as ImageIcon,
  LayoutGrid,
  MailCheck,
  Receipt,
  RefreshCw,
  ScrollText,
  Settings2,
  Shield,
  ShieldCheck,
  UserCheck,
  Users,
  Wallet,
  Webhook,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

type Access = "staff" | "admin" | "owner";

type AdminLink = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  access: Access;
};

type AdminGroup = { heading: string; links: AdminLink[] };

export const ADMIN_NAV_GROUPS: AdminGroup[] = [
  {
    heading: "Moderation",
    links: [
      { to: "/moderation", label: "Publication queue", icon: Shield, access: "staff" },
      { to: "/admin/members/pending", label: "Member applications", icon: UserCheck, access: "staff" },
      { to: "/admin/moderation/images", label: "Image moderation", icon: ImageIcon, access: "staff" },
      {
        to: "/admin/moderation/age-verification",
        label: "Age verification",
        icon: ShieldCheck,
        access: "staff",
      },
      { to: "/admin/tags", label: "Tag management", icon: Hash, access: "admin" },
    ],
  },
  {
    heading: "Finance",
    links: [
      { to: "/admin/finance", label: "Payouts & tips", icon: Wallet, access: "admin" },
      { to: "/admin/tip-actions", label: "Tip resolutions", icon: Receipt, access: "admin" },
      { to: "/admin/tip-replay", label: "Webhook replay", icon: RefreshCw, access: "admin" },
      { to: "/admin/webhook-health", label: "Webhook health", icon: Webhook, access: "admin" },
    ],
  },
  {
    heading: "Email",
    links: [
      { to: "/admin/email-delivery", label: "Delivery log", icon: MailCheck, access: "admin" },
      { to: "/admin/email-retries", label: "Retry queue", icon: AlarmClock, access: "admin" },
      { to: "/admin/email-templates", label: "Templates", icon: FileText, access: "admin" },
      { to: "/admin/email-analytics", label: "Deliverability", icon: ChartLine, access: "admin" },
    ],
  },
  {
    heading: "Team & platform",
    links: [
      { to: "/admin", label: "Admin overview", icon: LayoutGrid, access: "admin" },
      { to: "/admin/staff", label: "Team directory", icon: Users, access: "admin" },
      { to: "/admin/team", label: "Team management", icon: UserCheck, access: "owner" },
      { to: "/admin/audit", label: "Audit log", icon: ScrollText, access: "admin" },
      { to: "/admin/ops-settings", label: "Ops settings", icon: Settings2, access: "admin" },
    ],
  },
];

export function visibleAdminGroups(roles: {
  isStaff: boolean;
  isAdmin: boolean;
  isOwner: boolean;
}): AdminGroup[] {
  const allowed = (access: Access) => {
    if (access === "owner") return roles.isOwner;
    if (access === "admin") return roles.isAdmin || roles.isOwner;
    return roles.isStaff || roles.isAdmin || roles.isOwner;
  };
  return ADMIN_NAV_GROUPS.map((group) => ({
    ...group,
    links: group.links.filter((link) => allowed(link.access)),
  })).filter((group) => group.links.length > 0);
}

export function AdminNavMenu({
  isStaff,
  isAdmin,
  isOwner,
  className,
}: {
  isStaff: boolean;
  isAdmin: boolean;
  isOwner: boolean;
  className?: string;
}) {
  const groups = visibleAdminGroups({ isStaff, isAdmin, isOwner });
  if (groups.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="pill" className={cn("gap-1", className)}>
          <Shield className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Admin</span>
          <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-[70vh] w-64 overflow-y-auto"
      >
        {groups.map((group, index) => (
          <React.Fragment key={group.heading}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel className="text-xs uppercase tracking-wide text-muted-foreground">
              {group.heading}
            </DropdownMenuLabel>
            {group.links.map((link) => (
              <DropdownMenuItem asChild key={link.to}>
                <Link to={link.to} className="flex items-center gap-2">
                  <link.icon className="h-4 w-4" aria-hidden="true" />
                  {link.label}
                </Link>
              </DropdownMenuItem>
            ))}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
